import type { EngineInput, EngineResult } from './estimate.types';
import { resolveMaterialRate, type MaterialRateType } from './material.rates';
import {
  compareMaterialPriceToRegion,
  getRegionalMaterialBenchmark,
  type MaterialPriceComparison,
} from './regionalMaterialPrices';
import { multiplyMoney } from './money';

export type PaintMaterialStandard =
  | 'ECONOMY_ACRYLIC'
  | 'LATEX_STANDARD'
  | 'CERAMIC_WASHABLE'
  | 'PREMIUM_COLOR'
  | 'CUSTOM';

export interface MaterialSuggestion {
  id: string;
  label: string;
  category: string;
  quantity: number;
  unit: 'l' | 'kg' | 'szt' | 'm²' | 'mb' | 'm³';
  tier: 'economy' | 'standard' | 'premium';
  reason: string;
  estimatedUnitPrice?: number;
  estimatedTotal?: number;
  source: 'default' | 'user' | 'supplier';
  materialStandard?: PaintMaterialStandard;
  materialStandardLabel?: string;
  coatCount?: number;
  coveragePerLiter?: number;
  wastePercent?: number;
  clientUnitPriceNet?: number;
  contractorUnitPriceNet?: number;
  regionalPriceComparison?: MaterialPriceComparison;
}

function roundQuantity(value: number): number {
  return Math.ceil(value * 10) / 10;
}

function roundMaterialQuantity(value: number): number {
  return Math.round(value * 10) / 10;
}

function material(
  input: EngineInput,
  data: Omit<MaterialSuggestion, 'estimatedUnitPrice' | 'estimatedTotal' | 'source'> & {
    rateType?: MaterialRateType;
    estimatedUnitPrice?: number;
    source?: MaterialSuggestion['source'];
  }
): MaterialSuggestion {
  const estimatedUnitPrice = data.rateType
    ? resolveMaterialRate(data.rateType, input.userMaterialRates)
    : data.estimatedUnitPrice;
  const estimatedTotal = estimatedUnitPrice
    ? multiplyMoney(data.quantity, estimatedUnitPrice)
    : undefined;

  return {
    id: data.id,
    label: data.label,
    category: data.category,
    quantity: data.quantity,
    unit: data.unit,
    tier: data.tier,
    reason: data.reason,
    estimatedUnitPrice,
    estimatedTotal,
    source: data.source ?? (input.userMaterialRates && data.rateType && input.userMaterialRates[data.rateType] ? 'user' : 'default'),
    materialStandard: data.materialStandard,
    materialStandardLabel: data.materialStandardLabel,
    coatCount: data.coatCount,
    coveragePerLiter: data.coveragePerLiter,
    wastePercent: data.wastePercent,
    clientUnitPriceNet: data.clientUnitPriceNet,
    contractorUnitPriceNet: data.contractorUnitPriceNet,
    regionalPriceComparison: data.regionalPriceComparison,
  };
}

export function suggestMaterials(input: EngineInput, result: EngineResult): MaterialSuggestion[] {
  const tradeEngine = input.tradeEngine ?? (input.trade === 'lazienka' ? 'bathroom' : input.trade === 'malarz' ? 'painting' : 'painting');
  const area = result.meta.totalWorkingArea;
  const items = result.items;
  const suggestions: MaterialSuggestion[] = [];

  if (tradeEngine === 'painting') {
    if (items.some((item) => item.category === 'grunt')) {
      suggestions.push(material(input, {
        id: 'primer_l',
        label: 'Grunt głęboko penetrujący',
        category: 'primer',
        quantity: roundQuantity(area / 8),
        unit: 'l',
        tier: 'standard',
        reason: 'Gruntowanie jest w zakresie prac.',
        rateType: 'primer_l',
      }));
    }
    if (items.some((item) => item.category === 'gladz')) {
      suggestions.push(material(input, {
        id: 'plaster_kg',
        label: 'Gładź szpachlowa',
        category: 'plaster',
        quantity: roundQuantity(area * 2),
        unit: 'kg',
        tier: 'standard',
        reason: 'Przyjęto około 2 kg/m² dla dwóch warstw.',
        rateType: 'plaster_kg',
      }));
      suggestions.push(material(input, {
        id: 'sandpaper_szt',
        label: 'Papier ścierny / siatka',
        category: 'sandpaper',
        quantity: roundQuantity(area / 8),
        unit: 'szt',
        tier: 'standard',
        reason: 'Gładź wymaga materiału do szlifowania.',
      }));
    }
    if (items.some((item) => item.category === 'malowanie')) {
      const paint = calculatePaintingMaterial({
        workingArea: area,
        region: input.region,
        userUnitPriceNet: input.userMaterialRates?.paint_l,
      });
      suggestions.push(material(input, {
        id: 'paint_l',
        label: paint.standardLabel,
        category: 'paint',
        quantity: paint.litersWithWaste,
        unit: 'l',
        tier: 'standard',
        reason: `Malowanie ${paint.coatCount}x: ${paint.paintableLayerArea.toFixed(1)} m² warstw, wydajność ${paint.coveragePerLiter} m²/l i ${paint.wastePercent}% zapasu.`,
        estimatedUnitPrice: paint.clientUnitPriceNet,
        source: input.userMaterialRates?.paint_l ? 'user' : 'default',
        materialStandard: paint.standard,
        materialStandardLabel: paint.standardLabel,
        coatCount: paint.coatCount,
        coveragePerLiter: paint.coveragePerLiter,
        wastePercent: paint.wastePercent,
        clientUnitPriceNet: paint.clientUnitPriceNet,
        contractorUnitPriceNet: paint.contractorUnitPriceNet,
        regionalPriceComparison: paint.regionalPriceComparison,
      }));
      suggestions.push(material(input, {
        id: 'painting_foil_m2',
        label: 'Folia malarska',
        category: 'protection',
        quantity: roundQuantity(area),
        unit: 'm²',
        tier: 'economy',
        reason: 'Zabezpieczenie podłóg i stolarki przed malowaniem.',
      }));
      suggestions.push(material(input, {
        id: 'masking_tape_mb',
        label: 'Taśma malarska',
        category: 'protection',
        quantity: roundQuantity(area * 0.6),
        unit: 'mb',
        tier: 'standard',
        reason: 'Odcięcia i zabezpieczenia przy malowaniu.',
      }));
    }
  }

  if (tradeEngine === 'bathroom') {
    const tilesArea = input.tilesAreaM2 ?? 0;
    const hydroArea = input.hydroAreaM2 ?? tilesArea;
    if (hydroArea > 0) {
      suggestions.push(material(input, {
        id: 'hydro_kg',
        label: 'Hydroizolacja pod płytki',
        category: 'hydro',
        quantity: roundQuantity(hydroArea * 1.2),
        unit: 'kg',
        tier: 'standard',
        reason: 'Strefy mokre wymagają materiału hydroizolacyjnego.',
        rateType: 'hydro_kg',
      }));
    }
    if (tilesArea > 0) {
      suggestions.push(material(input, {
        id: 'tile_adhesive_kg',
        label: 'Klej do płytek',
        category: 'adhesive',
        quantity: roundQuantity(tilesArea * 4),
        unit: 'kg',
        tier: 'standard',
        reason: 'Przyjęto roboczy seed około 4 kg/m².',
        rateType: 'tile_adhesive_kg',
      }));
      suggestions.push(material(input, {
        id: 'grout_kg',
        label: 'Fuga',
        category: 'grout',
        quantity: roundQuantity(tilesArea * 0.35),
        unit: 'kg',
        tier: 'standard',
        reason: 'Fuga zależy od formatu płytki, seed MVP do ręcznej korekty.',
        rateType: 'grout_kg',
      }));
      suggestions.push(material(input, {
        id: 'silicone_szt',
        label: 'Silikon sanitarny',
        category: 'silicone',
        quantity: 2,
        unit: 'szt',
        tier: 'standard',
        reason: 'Typowy zapas dla stref mokrych i narożników.',
        rateType: 'silicone_szt',
      }));
    }
  }

  if (tradeEngine === 'paving') {
    const pavingArea = input.pavingAreaM2 ?? 0;
    const edgeLength = input.edgeLengthM ?? 0;
    const baseM3 = pavingArea * ((input.baseThicknessCm ?? 25) / 100);
    const beddingM3 = pavingArea * ((input.beddingThicknessCm ?? 4) / 100);

    if (pavingArea > 0) {
      suggestions.push(material(input, {
        id: 'paving_stone_m2',
        label: 'Kostka brukowa',
        category: 'paving',
        quantity: roundQuantity(pavingArea * 1.05),
        unit: 'm²',
        tier: 'standard',
        reason: 'Dodano 5% zapasu na docinki i uszkodzenia.',
        rateType: 'paving_stone_m2',
      }));
      suggestions.push(material(input, {
        id: 'aggregate_m3',
        label: 'Kruszywo na podbudowę',
        category: 'base',
        quantity: roundQuantity(baseM3),
        unit: 'm³',
        tier: 'standard',
        reason: 'Podbudowa liczona z powierzchni i grubości warstwy.',
        rateType: 'aggregate_m3',
      }));
      suggestions.push(material(input, {
        id: 'sand_m3',
        label: 'Piasek / podsypka',
        category: 'bedding',
        quantity: roundQuantity(beddingM3),
        unit: 'm³',
        tier: 'standard',
        reason: 'Podsypka liczona z powierzchni i grubości warstwy.',
        rateType: 'sand_m3',
      }));
      suggestions.push(material(input, {
        id: 'geotextile_m2',
        label: 'Geowłóknina',
        category: 'geotextile',
        quantity: roundQuantity(pavingArea),
        unit: 'm²',
        tier: 'economy',
        reason: 'Przy podjazdach pomaga oddzielić grunt od podbudowy.',
        rateType: 'geotextile_m2',
      }));
    }
    if (edgeLength > 0) {
      suggestions.push({
        id: 'paving_edges_mb',
        label: 'Obrzeża / krawężniki',
        category: 'edges',
        quantity: roundQuantity(edgeLength),
        unit: 'mb',
        tier: 'standard',
        reason: 'Obrzeża stabilizują nawierzchnię i ograniczają rozjeżdżanie kostki.',
        source: 'default',
      });
    }
  }

  return suggestions;
}

export const PAINT_MATERIAL_STANDARDS: Record<PaintMaterialStandard, {
  label: string;
  coveragePerLiter: number;
  wastePercent: number;
  clientUnitPriceNet: number;
  contractorUnitPriceNet: number;
}> = {
  ECONOMY_ACRYLIC: {
    label: 'Farba akrylowa ekonomiczna, biała',
    coveragePerLiter: 9,
    wastePercent: 10,
    clientUnitPriceNet: 24,
    contractorUnitPriceNet: 20,
  },
  LATEX_STANDARD: {
    label: 'Farba lateksowa standard, biała / jasny kolor',
    coveragePerLiter: 10,
    wastePercent: 10,
    clientUnitPriceNet: 35,
    contractorUnitPriceNet: 29,
  },
  CERAMIC_WASHABLE: {
    label: 'Farba ceramiczna / zmywalna',
    coveragePerLiter: 10,
    wastePercent: 10,
    clientUnitPriceNet: 48,
    contractorUnitPriceNet: 40,
  },
  PREMIUM_COLOR: {
    label: 'Farba premium / kolor intensywny',
    coveragePerLiter: 9,
    wastePercent: 15,
    clientUnitPriceNet: 58,
    contractorUnitPriceNet: 48,
  },
  CUSTOM: {
    label: 'Farba własna / wskazana przez klienta',
    coveragePerLiter: 10,
    wastePercent: 10,
    clientUnitPriceNet: 35,
    contractorUnitPriceNet: 35,
  },
};

function calculatePaintingMaterial(input: {
  workingArea: number;
  region?: string;
  standard?: PaintMaterialStandard;
  userUnitPriceNet?: number;
}) {
  const standard = input.standard ?? 'LATEX_STANDARD';
  const standardConfig = PAINT_MATERIAL_STANDARDS[standard];
  const coatCount = 2;
  const benchmark = getRegionalMaterialBenchmark('paint_l', input.region);
  const clientUnitPriceNet =
    input.userUnitPriceNet ??
    benchmark?.avgNet ??
    standardConfig.clientUnitPriceNet;
  const contractorUnitPriceNet = Math.min(standardConfig.contractorUnitPriceNet, clientUnitPriceNet);
  const paintableLayerArea = input.workingArea * coatCount;
  const litersRaw = paintableLayerArea / standardConfig.coveragePerLiter;
  const litersWithWaste = roundMaterialQuantity(litersRaw * (1 + standardConfig.wastePercent / 100));

  return {
    standard,
    standardLabel: standardConfig.label,
    coatCount,
    coveragePerLiter: standardConfig.coveragePerLiter,
    wastePercent: standardConfig.wastePercent,
    paintableLayerArea,
    litersWithWaste,
    clientUnitPriceNet,
    contractorUnitPriceNet,
    regionalPriceComparison: compareMaterialPriceToRegion({
      materialId: 'paint_l',
      region: input.region,
      userUnitPriceNet: clientUnitPriceNet,
    }),
  };
}
